/** Server records shaped for the console's screens. A fact no endpoint answers stays empty. */

import type {
  ActivityManifest,
  AddedActivity,
  MountRecord,
  RequirementBinding,
  SpaceSummary,
} from "@nebulize/sdk";

import { isScalar } from "../../lib/settings";
import type {
  AddedActivityRow,
  CatalogEntry,
  ConsoleBinding,
  SettingField,
  SettingValue,
  SpaceRow,
  VersionAccess,
} from "../../types";

/** `PublishedVersion`, which carries the manifest a pinned version was published with. */
export interface PublishedVersion {
  activity_id: string;
  version: string;
  manifest: ActivityManifest;
  availability?: CatalogEntry["availability"];
  /** CAT-026: what this version reads and writes outside itself, derived from its manifest. */
  access?: VersionAccess;
}

/** The generated `default` is an index signature, which no scalar fits, and a list is an array. */
function held(value: unknown, list: boolean): SettingValue {
  if (isScalar(value)) return list ? [value] : value;
  if (Array.isArray(value)) return value.filter(isScalar);
  return list ? [] : "";
}

function fieldsOf(manifest: ActivityManifest): SettingField[] {
  return (manifest.settings ?? []).map((field) => ({
    ...field,
    default: held(field.default, field.type === "list"),
  }));
}

/** One catalog entry off one published version. */
export function entryOf(published: PublishedVersion): CatalogEntry {
  const { activity } = published.manifest;
  const entry: CatalogEntry = {
    activity_id: published.activity_id,
    name: activity.name,
    version: published.version,
    description: activity.description ?? "",
    is_bundle: published.manifest.bundle != null,
    publisher: "",
    versions: [published.version],
    manifest: published.manifest,
    settings: fieldsOf(published.manifest),
  };
  return {
    ...entry,
    ...(published.availability === undefined ? {} : { availability: published.availability }),
    ...(published.access === undefined ? {} : { access: published.access }),
  };
}

/** The id a binding points at, whichever kind of target it names. */
export function bindingOf(binding: RequirementBinding): ConsoleBinding {
  return {
    ...binding,
    target_label:
      binding.account_id ??
      binding.pairing_id ??
      binding.machine_id ??
      binding.grant_id ??
      binding.provider_added_activity_id ??
      "",
  };
}

/** The mount an added activity holds now: its newest, or none. */
export function mountFor(
  addedActivityId: string,
  mounts: readonly MountRecord[],
): MountRecord | null {
  return mounts.reduce<MountRecord | null>(
    (held, one) =>
      one.added_activity_id === addedActivityId &&
      (held === null || one.started_at > held.started_at)
        ? one
        : held,
    null,
  );
}

export function addedRowOf(
  added: AddedActivity,
  mount: MountRecord | null,
  bindings: ConsoleBinding[],
): AddedActivityRow {
  const settings: Record<string, SettingValue> = {};
  for (const [key, value] of Object.entries(added.settings ?? {})) {
    settings[key] = held(value, Array.isArray(value));
  }
  return {
    ...added,
    settings,
    bindings,
    gates: [],
    mount,
    root_added_activity_id: null,
    path: [],
    removed_with_root: false,
  };
}

export function spaceRowOf(summary: SpaceSummary): SpaceRow {
  return {
    space: summary.space,
    vertical: "",
    handle: null,
    connections: [],
    members: [],
    invitations: [],
    grants: [],
    permits: [],
  };
}
